import React, { useState } from 'react';
import { CreateTicketRequest, TicketPriority, TicketComplexity, TargetFilter } from '../../types/api';
import { Ticket as TicketIcon, Plus, Minus, AlertCircle } from 'lucide-react';

interface CreateTicketFormProps {
  onSubmit: (request: CreateTicketRequest) => Promise<void>;
  loading?: boolean;
}

export function CreateTicketForm({ onSubmit, loading = false }: CreateTicketFormProps) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [complexity, setComplexity] = useState<TicketComplexity>('Sr');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  // Target filter state
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [filterTagInput, setFilterTagInput] = useState('');
  const [namePattern, setNamePattern] = useState('');

  const [error, setError] = useState<string>('');

  const addTag = () => {
    if (tagInput && !tags.includes(tagInput)) {
      setTags([...tags, tagInput]);
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const addFilterTag = () => {
    if (filterTagInput && !filterTags.includes(filterTagInput)) {
      setFilterTags([...filterTags, filterTagInput]);
      setFilterTagInput('');
    }
  };

  const removeFilterTag = (tag: string) => {
    setFilterTags(filterTags.filter((t) => t !== tag));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!body.trim()) {
      setError('Description is required');
      return;
    }

    const targetFilter: TargetFilter = {};
    if (filterTags.length > 0) targetFilter.tags = filterTags;
    if (namePattern) targetFilter.namePattern = namePattern;

    const request: CreateTicketRequest = {
      title: title.trim(),
      body: body.trim(),
      priority,
      complexity,
      tags: tags.length > 0 ? tags : undefined,
      targetFilter: Object.keys(targetFilter).length > 0 ? targetFilter : undefined,
    };

    try {
      await onSubmit(request);
      // Reset form on success
      setTitle('');
      setBody('');
      setTags([]);
      setFilterTags([]);
      setNamePattern('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create ticket');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
      <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
        <TicketIcon className="h-6 w-6 text-blue-600" />
        <h2 className="text-xl font-bold text-gray-900">Create Ticket</h2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title *
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Brief description of the issue"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description *
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Detailed description of the issue, requirements, and acceptance criteria"
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          required
        />
      </div>

      {/* Priority and Complexity */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Priority
          </label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as TicketPriority)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Complexity
          </label>
          <select
            value={complexity}
            onChange={(e) => setComplexity(e.target.value as TicketComplexity)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="Jr">Junior (Jr)</option>
            <option value="Sr">Senior (Sr)</option>
            <option value="Lvl3">Level 3 (Lvl3)</option>
          </select>
        </div>
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tags
        </label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
            placeholder="Add tag"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="button"
            onClick={addTag}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg"
            >
              {tag}
              <button type="button" onClick={() => removeTag(tag)}>
                <Minus className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Target Filter */}
      <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-medium text-gray-900">Target Repositories</h3>

        {/* Filter Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Repository Tags
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={filterTagInput}
              onChange={(e) => setFilterTagInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFilterTag())}
              placeholder="Add repository tag"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={addFilterTag}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {filterTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg"
              >
                {tag}
                <button type="button" onClick={() => removeFilterTag(tag)}>
                  <Minus className="h-4 w-4" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Name Pattern */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Name Pattern (regex)
          </label>
          <input
            type="text"
            value={namePattern}
            onChange={(e) => setNamePattern(e.target.value)}
            placeholder="e.g., ^content-.*"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Leave empty to create tickets in all repositories
          </p>
        </div>
      </div>

      {/* Submit */}
      <div className="flex gap-3 pt-4 border-t border-gray-200">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating...' : 'Create Ticket'}
        </button>
      </div>
    </form>
  );
}
